/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * RGBARRY-CUSTOMIZATION: site-wide "promo code no longer valid" dialog.
 *
 * Production shoppers kept retrying Place Order (up to seven times) without noticing the inline
 * note, so the inline banner alone is not enough. Mounted once in the root layout, this opens as
 * soon as the shared basket carries a no-longer-valid coupon: on cart or checkout load, after the
 * PayPal initiate check, and after a rejected Place Order (every path publishes the recalculated
 * basket). Opening it also revalidates the page, so a route rendering from its own loader basket
 * (the cart) shows the banner and hides the wallet buttons too. It closes by itself once the codes
 * are removed. Dismissing it keeps the inline banner and the blocked buttons; it reopens only for a
 * different set of invalid codes.
 */
import { lazy, Suspense, useEffect, useRef, useState, type ReactElement } from 'react';
import { useRevalidator } from 'react-router';
import { useBasket } from '@/providers/basket';
import { getInvalidCoupons } from '@/lib/cart/coupon-status';

const InvalidCouponDialogContent = lazy(() => import('./invalid-coupon-dialog-content'));

export function InvalidCouponDialog(): ReactElement | null {
    const basket = useBasket();
    const invalidCoupons = getInvalidCoupons(basket);
    const basketId = basket?.basketId;
    const invalidKey = basketId
        ? `${basketId}:${invalidCoupons
              .map((item) => item.couponItemId ?? item.code)
              .sort()
              .join(',')}`
        : '';
    const [dismissedKey, setDismissedKey] = useState<string | null>(null);
    const revalidator = useRevalidator();
    const revalidatedKeyRef = useRef('');

    // Once per invalid set: a route loader may still hold the pre-check basket (for example after the
    // PayPal initiate check, which is a plain fetch rather than a router action).
    useEffect(() => {
        if (invalidCoupons.length === 0 || revalidatedKeyRef.current === invalidKey) return;
        revalidatedKeyRef.current = invalidKey;
        void revalidator.revalidate();
        // revalidator identity changes with its state; keying on the invalid set avoids a loop
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidKey, invalidCoupons.length]);

    if (!basketId || invalidCoupons.length === 0 || dismissedKey === invalidKey) return null;

    return (
        <Suspense fallback={null}>
            <InvalidCouponDialogContent
                basketId={basketId}
                invalidCoupons={invalidCoupons}
                onDismiss={() => setDismissedKey(invalidKey)}
            />
        </Suspense>
    );
}
